import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import emailjs from '@emailjs/browser';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { MailIcon, PhoneIcon, MapPinIcon, ArrowDownIcon, ShieldCheckIcon, ClipboardCheckIcon, PlaneIcon, CheckCircle2Icon, MessageCircleIcon } from 'lucide-react';
// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
// Header slideshow images
const headerImages = ['https://cdn.magicpatterns.com/uploads/9wrfMpLubkCpTgrLHMj6DN/andy-wang-ogUyaf8JWA4-unsplash.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/xkcnd2wwVAY9t9Dw3pT4pE/grant-durr-aJEWd6QDfSE-unsplash.jpg', 'https://cdn.magicpatterns.com/uploads/7ftDnahXPUPgQHkmn3BV78/pandu-agus-wismoyo-7OgQ-Ze7BXQ-unsplash.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/u4QHFdWKSmdiaJGSPoptZN/thisisengineering-yhCHx8Mc-Kc-unsplash.jpg'];
export function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    inquiryType: 'Aircraft Management',
    message: '',
    consent: false
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showToast, setShowToast] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sections = useRef<Element[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Office locations with coordinates
  const officeLocations = [{
    name: 'Head Office',
    address: '1 Liang Seah Street, #02-02, Singapore 189022.',
    position: [1.297, 103.8536] as [number, number],
    phone: '+65 8126 6622',
    fax: '+65 6234 3152',
    email: 'contact@cosmoimageaviation.co'
  }, {
    name: 'Airport Warehouse',
    address: '115 Airport Cargo Road, #01-01 & #01-06, Cargo Agents Building C, Singapore 819466.',
    position: [1.3644, 103.9915] as [number, number],
    phone: '+65 8126 6622 / +65 9663 0738',
    fax: '+65 6543 1130'
  }];
  // Slideshow effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % headerImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);
  // Setup intersection observer for active section highlighting
  useEffect(() => {
    observerRef.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, {
      threshold: 0.3
    });
    const sectionElements = document.querySelectorAll('section[id]');
    sections.current = Array.from(sectionElements);
    sections.current.forEach(section => {
      observerRef.current?.observe(section);
    });
    return () => {
      sections.current.forEach(section => {
        observerRef.current?.unobserve(section);
      });
    };
  }, []);
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  // Validate form
  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.message.trim()) errors.message = 'Message is required';
    if (!formData.consent) errors.consent = 'You must consent to contact';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Handle form submission with EmailJS
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const serviceId = 'YOUR_EMAILJS_SERVICE_ID';
        const templateId = 'YOUR_EMAILJS_TEMPLATE_ID';
        const publicKey = 'YOUR_EMAILJS_PUBLIC_KEY';
        const templateParams = {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          company: formData.company || 'Not provided',
          inquiry_type: formData.inquiryType,
          message: formData.message,
          to_email: 'contact@cosmoimageaviation.co'
        };
        await emailjs.send(serviceId, templateId, templateParams, publicKey);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          inquiryType: 'Aircraft Management',
          message: '',
          consent: false
        });
      } catch (error) {
        console.error('Failed to send email:', error);
        alert('Failed to send email. Please try again later or contact us directly.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <>
      <div className="min-h-screen w-full bg-white font-sans selection:bg-navy selection:text-white" data-id="element-0">
        <Header activeSection={activeSection} onNavigate={scrollToSection} data-id="element-1" />
        <main data-id="element-2">
          {/* Hero Section */}
          <section id="home" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden isolate" data-id="element-3">
            {/* Slideshow backgrounds */}
            {headerImages.map((img, index) => <div key={index} className={`absolute inset-0 w-full h-full transition-opacity duration-[2000ms] ease-in-out bg-cover bg-center z-0 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`} style={{
            backgroundImage: `url(${img})`
          }} data-id="element-4"></div>)}

            {/* Navy Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-navy/50 via-navy/40 to-navy/70 z-[1]" data-id="element-5"></div>

            <div className="container mx-auto px-6 relative z-10 text-center" data-id="element-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-tight leading-tight" data-id="element-7">
                <span className="font-light" data-id="element-8">Professional Aviation</span>
                <br data-id="element-9" />
                <span className="font-bold" data-id="element-10">Solutions You Can Trust</span>
              </h1>

              <h2 className="text-sm sm:text-lg md:text-xl text-white/80 font-medium tracking-widest uppercase mb-8" data-id="element-11">
                Aircraft Management · CAMO Services · Charter Solutions
              </h2>

              <p className="text-sm sm:text-base md:text-lg mb-10 sm:mb-14 max-w-2xl mx-auto text-white/60 font-light leading-relaxed px-2 sm:px-0" data-id="element-12">
                Delivering reliable, compliant, and cost-efficient aviation
                services across Indonesia and the region.
              </p>

              <button onClick={() => scrollToSection('contact')} className="bg-white text-navy hover:bg-white/90 hover:scale-105 hover:shadow-xl rounded-full font-semibold py-3.5 px-10 text-sm uppercase tracking-wider transition-all duration-300" data-id="element-13">
                Talk to Us
              </button>
            </div>

            {/* Scroll Indicator */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-6" data-id="element-14">
              <div className="flex gap-2" data-id="element-15">
                {headerImages.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2 h-2 rounded-full transition-all duration-500 ${index === currentSlide ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/70'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-16"></button>)}
              </div>
              <ArrowDownIcon className="w-5 h-5 text-white/70 animate-scroll-bounce" data-id="element-17" />
            </div>
          </section>

          {/* About Us Section */}
          <section id="about" className="py-16 sm:py-24 md:py-32 bg-white" data-id="element-18">
            <div className="container mx-auto max-w-5xl px-4 sm:px-6 text-center" data-id="element-19">
              <img src="https://cdn.magicpatterns.com/uploads/9Pc1EdXJ5Kiaov2hDznTrF/CIA_Logo_latest.png" alt="Cosmo Image Aviation Logo" className="h-16 sm:h-20 mx-auto mb-8" data-id="element-20" />
              <span className="text-navy/50 font-bold tracking-widest uppercase text-xs block mb-4" data-id="element-21">
                ABOUT US
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-navy mb-8 sm:mb-12" data-id="element-22">
                <span className="font-light" data-id="element-23">Your Trusted</span>{' '}
                <span className="font-bold" data-id="element-24">Aviation Partner</span>
              </h2>

              <div className="space-y-6 sm:space-y-8 text-slate text-base sm:text-lg md:text-xl font-light leading-relaxed max-w-4xl mx-auto" data-id="element-25">
                <p data-id="element-26">
                  Cosmo Image Aviation is an aviation services company
                  specializing in aircraft management, CAMO, and charter. We
                  support aircraft owners with focus on safety, compliance,
                  operational efficiency, and cost transparency.
                </p>
                <p data-id="element-27">
                  Your trusted aviation partner — protecting your asset and
                  optimizing performance across Indonesia and the region.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mt-16 sm:mt-20 pt-12 sm:pt-16 border-t border-gray-100" data-id="element-28">
                {[{
                label: 'Founded',
                value: '2023'
              }, {
                label: 'Headquarters',
                value: 'Singapore'
              }, {
                label: 'Focus',
                value: 'Asia-Pacific'
              }, {
                label: 'Standards',
                value: 'International'
              }].map((stat, i) => <div key={i} className="text-center group cursor-default" data-id="element-29">
                    <div className="text-xl sm:text-2xl md:text-3xl font-bold text-navy mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300" data-id="element-30">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm font-medium text-slate uppercase tracking-wider group-hover:text-navy transition-colors duration-300" data-id="element-31">
                      {stat.label}
                    </div>
                  </div>)}
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section id="services" className="py-16 sm:py-24 md:py-32 bg-surface" data-id="element-32">
            <div className="container mx-auto max-w-[var(--maxw)] px-4 sm:px-6" data-id="element-33">
              <div className="text-center mb-16" data-id="element-34">
                <span className="text-navy/50 font-bold tracking-widest uppercase text-xs block mb-4" data-id="element-35">
                  OUR SERVICES
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-navy" data-id="element-36">
                  <span className="font-light" data-id="element-37">Comprehensive</span>{' '}
                  <span className="font-bold" data-id="element-38">Aviation Solutions</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-39">
                {/* Service 1 */}
                <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 border border-gray-100/80 flex flex-col h-full group" data-id="element-40">
                  <div className="w-12 h-12 bg-navy/5 group-hover:bg-navy rounded-xl flex items-center justify-center mb-8 transition-colors duration-500" data-id="element-41">
                    <ShieldCheckIcon className="w-7 h-7 text-navy group-hover:text-white transition-colors duration-500" data-id="element-42" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-43">
                    Aircraft Management
                  </h3>
                  <p className="text-slate font-light leading-relaxed mb-8 flex-grow" data-id="element-44">
                    End-to-end management including operations, crew,
                    maintenance coordination, cost control, and compliance — so
                    you can focus on what matters most.
                  </p>
                  <ul className="space-y-3" data-id="element-45">
                    {['Operational oversight & crew management', 'Flight planning & scheduling', 'Maintenance coordination', 'Financial management & cost reporting', 'Regulatory compliance'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-46">
                        <CheckCircle2Icon className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" data-id="element-47" />
                        <span className="text-sm text-slate" data-id="element-48">{item}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Service 2 */}
                <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 border border-gray-100/80 flex flex-col h-full group" data-id="element-49">
                  <div className="w-12 h-12 bg-navy/5 group-hover:bg-navy rounded-xl flex items-center justify-center mb-8 transition-colors duration-500" data-id="element-50">
                    <ClipboardCheckIcon className="w-7 h-7 text-navy group-hover:text-white transition-colors duration-500" data-id="element-51" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-52">
                    CAMO Services
                  </h3>
                  <p className="text-slate font-light leading-relaxed mb-8 flex-grow" data-id="element-53">
                    Continuing airworthiness management, maintenance planning,
                    documentation control, and regulatory liaison — ensuring
                    your aircraft meets all requirements at all times.
                  </p>
                  <ul className="space-y-3" data-id="element-54">
                    {['Airworthiness reviews & documentation', 'Maintenance program management', 'AD & SB compliance tracking', 'Regulatory liaison & reporting', 'Technical records management'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-55">
                        <CheckCircle2Icon className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" data-id="element-56" />
                        <span className="text-sm text-slate" data-id="element-57">{item}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Service 3 */}
                <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 border border-gray-100/80 flex flex-col h-full group" data-id="element-58">
                  <div className="w-12 h-12 bg-navy/5 group-hover:bg-navy rounded-xl flex items-center justify-center mb-8 transition-colors duration-500" data-id="element-59">
                    <PlaneIcon className="w-7 h-7 text-navy group-hover:text-white transition-colors duration-500" data-id="element-60" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-61">
                    Aircraft Charter
                  </h3>
                  <p className="text-slate font-light leading-relaxed mb-8 flex-grow" data-id="element-62">
                    Safe, reliable charter services with professional crew and
                    well-maintained aircraft for corporate travel, special
                    missions, and ad-hoc requirements.
                  </p>
                  <ul className="space-y-3" data-id="element-63">
                    {['Business jet charter', 'Turboprop solutions', 'Corporate travel management', 'Special mission flights', 'Regional & international routing'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-64">
                        <CheckCircle2Icon className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" data-id="element-65" />
                        <span className="text-sm text-slate" data-id="element-66">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Why Choose Us Section */}
          <section id="why" className="py-16 sm:py-24 md:py-32 bg-white" data-id="element-67">
            <div className="container mx-auto max-w-[var(--maxw)] px-4 sm:px-6" data-id="element-68">
              <div className="text-center mb-16" data-id="element-69">
                <span className="text-navy/50 font-bold tracking-widest uppercase text-xs block mb-4" data-id="element-70">
                  WHY CHOOSE US
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-navy" data-id="element-71">
                  <span className="font-light" data-id="element-72">The Cosmo Image</span>{' '}
                  <span className="font-bold" data-id="element-73">Aviation Difference</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 sm:gap-x-12 gap-y-10 sm:gap-y-16 max-w-5xl mx-auto" data-id="element-74">
                {[{
                title: 'Safety First',
                desc: 'Safety is our non-negotiable priority. Every operation, every decision, every procedure is guided by the highest safety standards.'
              }, {
                title: 'Operational Expertise',
                desc: 'Deep aviation expertise backed by years of industry experience. We understand the complexities of aircraft operations inside and out.'
              }, {
                title: 'Cost Transparency',
                desc: 'Complete visibility into your aircraft operating costs. No hidden fees, no surprises – just clear, honest financial reporting.'
              }, {
                title: 'Personalized Service',
                desc: 'Every aircraft owner has unique needs. We tailor our services to match your specific requirements and operational goals.'
              }, {
                title: 'Regional Knowledge',
                desc: 'Strong understanding of Indonesian aviation environment, regulations, and operational conditions, combined with international standards.'
              }, {
                title: '24/7 Availability',
                desc: 'Round-the-clock support for all operational needs. Our dedicated team is always ready to respond, ensuring your aircraft operations never miss a beat.'
              }].map((value, index) => <div key={index} className="relative pl-6 border-l-2 border-navy/20 hover:border-navy hover:translate-x-1 transition-all duration-300 cursor-default" data-id="element-75">
                    <h3 className="text-xl font-bold text-navy mb-3" data-id="element-76">
                      {value.title}
                    </h3>
                    <p className="text-slate font-light leading-relaxed text-sm" data-id="element-77">
                      {value.desc}
                    </p>
                  </div>)}
              </div>
            </div>
          </section>

          {/* Fleet Access Section */}
          <section id="fleet" className="relative py-16 sm:py-24 md:py-32 overflow-hidden" data-id="element-78">
            {/* Background Image */}
            <div className="absolute inset-0 bg-cover bg-center" style={{
            backgroundImage: `url('https://cdn.magicpatterns.com/uploads/btZUgGy9bLWeEW1LHW3e5q/oskar-kadaksoo-i8fRj3z8bvk-unsplash.jpg')`
          }} data-id="element-79"></div>
            <div className="absolute inset-0 bg-navy/60" data-id="element-80"></div>

            <div className="container mx-auto max-w-[var(--maxw)] px-4 sm:px-6 relative z-10" data-id="element-81">
              <div className="flex flex-col lg:flex-row gap-10 sm:gap-16 items-center" data-id="element-82">
                <div className="lg:w-1/2" data-id="element-83">
                  <span className="text-white/50 font-bold tracking-widest uppercase text-xs block mb-4" data-id="element-84">
                    FLEET ACCESS
                  </span>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-6" data-id="element-85">
                    <span className="font-light" data-id="element-86">Access to</span>{' '}
                    <span className="font-bold" data-id="element-87">Premium Aircraft</span>
                  </h2>
                  <p className="text-white/70 font-light leading-relaxed text-base sm:text-lg mb-8" data-id="element-88">
                    Access to business jets and turboprops through managed
                    aircraft and partner network, suited for various mission
                    profiles across Indonesia and the region.
                  </p>
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/10" data-id="element-89">
                    <p className="text-sm text-white/60 italic" data-id="element-90">
                      * Aircraft availability is subject to scheduling,
                      operational requirements, and owner approval.
                    </p>
                  </div>
                </div>

                <div className="lg:w-1/2 w-full grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-91">
                  <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:bg-white/20 hover:border-white/25 hover:-translate-y-1 transition-all duration-300" data-id="element-92">
                    <h3 className="text-xl font-bold text-white mb-3" data-id="element-93">
                      Business Jets
                    </h3>
                    <p className="text-sm text-white/60 font-light" data-id="element-94">
                      Premium comfort and speed for corporate travel and
                      executive transport across international routes.
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:bg-white/20 hover:border-white/25 hover:-translate-y-1 transition-all duration-300" data-id="element-95">
                    <h3 className="text-xl font-bold text-white mb-3" data-id="element-96">
                      Turboprops
                    </h3>
                    <p className="text-sm text-white/60 font-light" data-id="element-97">
                      Efficient and versatile solutions ideal for regional
                      connectivity and accessing shorter runways.
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/10 sm:col-span-2 hover:bg-white/20 hover:border-white/25 hover:-translate-y-1 transition-all duration-300" data-id="element-98">
                    <h3 className="text-xl font-bold text-white mb-3" data-id="element-99">
                      Special Missions
                    </h3>
                    <p className="text-sm text-white/60 font-light" data-id="element-100">
                      Configured aircraft available for specialized operational
                      requirements and unique mission profiles.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Leadership Section */}
          <section id="leadership" className="py-16 sm:py-24 md:py-32 bg-white" data-id="element-101">
            <div className="container mx-auto max-w-[var(--maxw)] px-4 sm:px-6" data-id="element-102">
              <div className="text-center mb-20" data-id="element-103">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-navy" data-id="element-104">
                  <span className="font-light" data-id="element-105">Our</span>{' '}
                  <span className="font-bold" data-id="element-106">Leadership</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10" data-id="element-107">
                {[{
                initials: 'LKP',
                name: 'Lee Kok Ping',
                role: 'Managing Director / Director'
              }, {
                initials: 'LMH',
                name: 'Lee Ming Han',
                role: 'Director'
              }, {
                initials: 'TYC',
                name: 'Tan Yong Chuan (Ricky)',
                role: 'Director'
              }, {
                initials: 'TTK',
                name: 'Thorng Tohkingkeo',
                role: 'Company Secretary'
              }].map((leader, index) => <div key={index} className="text-center group hover:-translate-y-1 transition-all duration-300" itemScope itemType="https://schema.org/Person" data-id="element-108">
                    <div className="w-20 h-20 mx-auto bg-surface rounded-full flex items-center justify-center text-lg font-bold text-navy mb-6 group-hover:bg-navy group-hover:text-white group-hover:scale-110 transition-all duration-300" data-id="element-109">
                      {leader.initials}
                    </div>
                    <h3 className="text-lg font-bold text-navy mb-1" itemProp="name" data-id="element-110">
                      {leader.name}
                    </h3>
                    <p className="text-slate font-medium text-xs uppercase tracking-wider" data-id="element-111">
                      {leader.role}
                    </p>
                  </div>)}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="py-16 sm:py-24 md:py-32 bg-navy" data-id="element-112">
            <div className="container mx-auto max-w-[var(--maxw)] px-4 sm:px-6" data-id="element-113">
              <div className="text-center mb-16" data-id="element-114">
                <span className="text-white/40 font-bold tracking-widest uppercase text-xs block mb-4" data-id="element-115">
                  GET IN TOUCH
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-6" data-id="element-116">
                  <span className="font-light" data-id="element-117">Start a</span>{' '}
                  <span className="font-bold" data-id="element-118">Conversation</span>
                </h2>
                <p className="text-gray-400 font-light text-base sm:text-lg max-w-2xl mx-auto" data-id="element-119">
                  Whether you need aircraft management, CAMO services, or
                  charter solutions, we are here to help.
                </p>
              </div>

              <div className="max-w-3xl mx-auto space-y-8" data-id="element-120">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-121">
                  <div className="bg-navy-light p-6 rounded-xl border border-white/5 hover:border-white/20 hover:-translate-y-1 transition-all duration-300" data-id="element-122">
                    <MailIcon className="w-6 h-6 text-white/70 mb-4" data-id="element-123" />
                    <h3 className="text-white font-semibold mb-2" data-id="element-124">Email</h3>
                    <a href="mailto:contact@cosmoimageaviation.co" className="text-gray-400 text-sm hover:text-white transition-colors break-all" data-id="element-125">
                      contact@cosmoimageaviation.co
                    </a>
                  </div>
                  <div className="bg-navy-light p-6 rounded-xl border border-white/5 hover:border-white/20 hover:-translate-y-1 transition-all duration-300" data-id="element-126">
                    <PhoneIcon className="w-6 h-6 text-white/70 mb-4" data-id="element-127" />
                    <h3 className="text-white font-semibold mb-2" data-id="element-128">Phone</h3>
                    <a href="tel:+6581266622" className="text-gray-400 text-sm hover:text-white transition-colors block" data-id="element-129">
                      +65 8126 6622
                    </a>
                    <span className="text-gray-500 text-xs" data-id="element-130">
                      Fax: +65 6234 3152
                    </span>
                  </div>
                  <div className="bg-navy-light p-6 rounded-xl border border-white/5 sm:col-span-2 hover:border-white/20 hover:-translate-y-1 transition-all duration-300" data-id="element-131">
                    <MapPinIcon className="w-6 h-6 text-white/70 mb-4" data-id="element-132" />
                    <h3 className="text-white font-semibold mb-2" data-id="element-133">
                      Head Office
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-134">
                      1 Liang Seah Street, #02-02
                      <br data-id="element-135" />
                      Singapore 189022
                    </p>
                  </div>
                </div>

                <div className="h-[300px] w-full rounded-xl overflow-hidden border border-white/5" data-id="element-136">
                  <MapContainer center={[1.297, 103.8536]} zoom={13} style={{
                  height: '100%',
                  width: '100%'
                }} scrollWheelZoom={false} data-id="element-137">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" data-id="element-138" />
                    {officeLocations.map((location, index) => <Marker key={index} position={location.position} data-id="element-139">
                        <Popup data-id="element-140">
                          <div className="text-center" data-id="element-141">
                            <strong className="block mb-1 text-navy" data-id="element-142">
                              {location.name}
                            </strong>
                            <span className="text-slate text-xs" data-id="element-143">
                              {location.phone}
                            </span>
                          </div>
                        </Popup>
                      </Marker>)}
                  </MapContainer>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer data-id="element-144" />
      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/6581266622" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 flex items-center justify-center group" aria-label="Chat on WhatsApp" data-id="element-145">
        <MessageCircleIcon className="w-6 h-6" data-id="element-146" />
        <span className="hidden sm:block absolute right-full mr-4 bg-white text-navy text-xs font-semibold px-3 py-1.5 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" data-id="element-147">
          Talk to Us
        </span>
      </a>
    </>;
}